import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { SignupGDPRCompliance } from '@/signupElement/SignupGDPRCompliance.js';

import SimpleModal from '../ui/SimpleModal/index.js';

import SurveyRenderer from '../surveyElement/SurveyRenderer.js';
import {
  freeProductCheckoutModalContentContainer,
  freeProductCheckoutModalDialogContent,
} from './FreeProductCheckoutModal.css.js';
import type { FreeProductCheckoutModalElementProps } from './FreeProductCheckoutModal.type.js';

export function FreeProductCheckoutModalElement(props: FreeProductCheckoutModalElementProps) {
  const {
    element,
    isOpen = false,
    onOpenChange,
    isPreviewing = false,
    isLoading,
    translations,
    onEmailChange,
    hasTermsOfService,
    hasCustomTermsOfService,
    customTermsOfServiceText,
    defaultTOSConsentValue,
    gdprTranslations,
    form,
    hideEmailInput = false,
  } = props;
  const {
    gap,
    width,
    maxWidth,
    padding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    modalOverlayBackgroundColor,
    modalCloseButtonColor = 'inherit',
    modalCloseButtonPlacementTop,
    modalCloseButtonPlacementRight,
  } = element.attrs || {};

  const generatedStyle = {
    '--overlay-background-color': modalOverlayBackgroundColor,
    '--modal-close-button-color': modalCloseButtonColor,
    '--modal-close-button-placement-top': modalCloseButtonPlacementTop || '24px',
    '--modal-close-button-placement-right': modalCloseButtonPlacementRight || '24px',
  } as CSSProperties;

  const renderedElement = (
    <div
      className={classNames(freeProductCheckoutModalContentContainer, 'flex flex-col h-auto text-left')}
      style={
        {
          '--width': width,
          '--max-width': maxWidth,
          backgroundColor,
          padding,
          borderStyle,
          borderWidth,
          borderRadius,
          borderColor,
          gap,
          '--padding': padding,
        } as CSSProperties
      }
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-gray-900 text-2xl font-semibold m-0 p-0">
          {translations?.['products.free_product_checkout.title']}
        </h2>
        <p className="text-gray-600 text-sm m-0 p-0">{translations?.['products.free_product_checkout.description']}</p>
      </div>
      <div className="flex flex-col gap-4">
        {!hideEmailInput && (
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.['products.free_product_checkout.email_label']}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm placeholder:text-[#374151]"
              placeholder={translations?.['products.free_product_checkout.email_placeholder']}
              onChange={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
            />
          </div>
        )}
        {form && (
          <div
            className=""
            style={
              {
                '--gap': '16px',
                '--text-align': 'left',
                '--question-required-color': '#000',
                '--question-label-color': '#374151',
                '--question-label-font-family': 'inherit',
                '--question-label-font-size': '14px',
                '--input-text-font-size': '14px',
                '--input-text-color': '#374151',
                '--input-placeholder-color': '#374151',
                '--question-label-font-weight': '500',
                '--question-label-text-transform': 'none',
                '--question-label-text-decoration': 'none',
                '--question-label-text-decoration-color': '#000',
                '--question-label-text-decoration-style': 'solid',
                '--question-label-text-decoration-thickness': '1px',
                '--question-label-text-decoration-skip-ink': 'none',
                '--question-label-text-decoration-skip-ink-color': '#000',
                '--question-label-text-decoration-skip-ink-style': 'solid',
                '--question-label-text-decoration-skip-ink-thickness': '1px',
              } as CSSProperties
            }
          >
            <SurveyRenderer
              email={''}
              hasLocalStorageEmail={false}
              survey={form}
              isSubmitting={isLoading}
              showSubmitButton={false}
              showEmailInput={false}
              showQuestionNumber={false}
            />
          </div>
        )}
        {hasTermsOfService && !hideEmailInput && (
          <SignupGDPRCompliance
            hasCustomTermsOfService={hasCustomTermsOfService}
            customTermsOfServiceText={customTermsOfServiceText}
            defaultTOSConsentValue={defaultTOSConsentValue}
            gdprTranslations={gdprTranslations}
          />
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading
            ? translations?.['products.free_product_checkout.submitting']
            : translations?.['products.free_product_checkout.submit']}
        </button>
      </div>
    </div>
  );

  if (isPreviewing) {
    return (
      <div className="w-full h-auto min-h-[600px] flex items-center justify-center relative" style={generatedStyle}>
        <div className="absolute top-0 left-0 w-full h-full bg-[--overlay-background-color]">&nbsp;</div>
        <div className="z-10 relative">{renderedElement}</div>
      </div>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onOpenChange}
      style={generatedStyle}
      className="shadow-lg"
      showCloseButton={!isLoading}
      hideOverflow={false}
      shouldCloseOnOutsideClick={!isLoading}
    >
      <div className={classNames(freeProductCheckoutModalDialogContent)}>{renderedElement}</div>
    </SimpleModal>
  );
}
